import { NewBadge } from "design-system/molecules/NewBadge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { FormControl, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { getCompatibleTriggers } from "modules/campaigns/lib/getCompatibleTriggers"
import { Fragment } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { type CampaignTriggerType } from "schema"
import { z } from "zod"
import { getTriggerIntegrationWithEmail } from "./forms/campaignTriggerEmail"
import { useTriggerKind } from "./hooks/useTriggerKind"

type CampaignFormValues = z.infer<typeof UpdateCampaignSchemaFrontend>

export function TriggerSelect({ isEditable }: { isEditable: boolean }) {
  const { t } = useTranslation()
  const form = useFormContext<CampaignFormValues>()
  const { items } = useTriggerKind()
  const flow = form.watch("flow")
  const channel = form.watch("channel")

  const itemsArray = getCompatibleTriggers(channel, flow)
    .map((tr) => items[tr])
    .filter((item): item is NonNullable<typeof item> => Boolean(item && item.active !== false))

  return (
    <FormField
      control={form.control}
      name="trigger"
      render={({ field }) => {
        const selectedItem = field.value ? items[field.value as keyof typeof items] : null

        return (
          <FormItem className="flex flex-col gap-2 h-full">
            <FormControl>
              <Select
                key={field.value ?? "empty"}
                value={field.value ?? undefined}
                onValueChange={(val) => {
                  const trigger = val as CampaignTriggerType
                  field.onChange(trigger, {
                    shouldDirty: true,
                  })
                  form.setValue(
                    "triggerIntegration",
                    getTriggerIntegrationWithEmail(form.getValues("triggerIntegration"), trigger),
                    { shouldDirty: true },
                  )
                }}
                disabled={!isEditable}
              >
                <SelectTrigger className="h-full py-3 [&>span]:line-clamp-none [&>span]:min-w-0 [&>span]:w-full">
                  <SelectValue placeholder={t("campaigns.trigger.kind.placeholder")}>
                    {selectedItem ? (
                      <div className="flex flex-col gap-1.5 text-left min-w-0 w-full">
                        <div className="flex items-center gap-2 min-w-0">
                          <selectedItem.icon className="w-5 h-5 shrink-0" />
                          <span className="text-sm font-medium truncate">{selectedItem.label}</span>
                        </div>
                        <span className="text-xs text-muted-foreground truncate">
                          {selectedItem.description}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">
                        {t("campaigns.trigger.kind.placeholder")}
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {itemsArray.map((item, index) => (
                    <Fragment key={item.value}>
                      <SelectItem value={item.value} disabled={item.enabled === false}>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <item.icon className="w-5 h-5" />
                            <span className="text-base font-medium">{item.label}</span>
                            {item.beta && <NewBadge label={t("shared.new")} />}
                          </div>
                          <span className="text-sm text-muted-foreground">{item.description}</span>
                        </div>
                      </SelectItem>
                      {index < itemsArray.length - 1 && <SelectSeparator />}
                    </Fragment>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
